/**
 * The heading ids of the docs come from markdown-it-anchor, which applies `encodeURIComponent`
 * to the heading text. An id can thus hold percent escapes, such as `%E3%83%97` or `%3A`, and
 * literal parentheses. The upload job of pos-agent shares this id function, so the id function
 * stays as it is and the lookup accepts each form of a hash:
 *
 * - the id itself, from a TOC link or a link in the docs
 * - the hash after vue-router decodes it (`route.hash`)
 * - the help URL of pos-backend or pos-agent, which encodes `(` and `)` as `%28` and `%29`
 *
 * useDocsTocScroll decides which hash to look up: the URL hash or `route.hash`.
 */

const safeDecode = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

// A lone surrogate makes encodeURIComponent throw.
const safeEncode = (value: string): string | null => {
  try {
    return encodeURIComponent(value);
  } catch {
    return null;
  }
};

/** The ids that a hash can mean, in the order of preference. */
export const headingIdCandidates = (hash: string): string[] => {
  const raw = hash.replace(/^#/, '');
  if (!raw) return [];

  const decoded = safeDecode(raw);
  const candidates = [raw, decoded, decoded === null ? null : safeEncode(decoded), safeEncode(raw)];
  return [...new Set(candidates.filter((id): id is string => id !== null))];
};

/**
 * True if `rawHash` (window.location.hash) is the URL hash of the navigation that gave
 * `routeHash`. vue-router decodes the hash of a string location twice and the hash of the URL
 * once, so route.hash is the raw hash decoded once or twice. Another value is the hash of an
 * older navigation.
 */
export const isHashOfRoute = (rawHash: string, routeHash: string): boolean => {
  const raw = rawHash.replace(/^#/, '');
  const route = routeHash.replace(/^#/, '');
  if (!raw) return false;
  const once = safeDecode(raw);
  return raw === route || once === route || (once !== null && safeDecode(once) === route);
};

/** The id of the heading in `root` that the hash points to, or null. */
export const findHeadingId = (root: ParentNode, hash: string): string | null => {
  const candidates = headingIdCandidates(hash);
  if (!candidates.length) return null;

  // A plain id comparison needs no CSS escape for the "%" and "(" of the ids.
  const ids = new Set(Array.from(root.querySelectorAll('[id]'), el => el.id));
  return candidates.find(id => ids.has(id)) ?? null;
};
